// Admin-only page to send every branded email template to a chosen
// recipient. Uses the same /lovable/email/transactional/send route as the
// production trigger points, so a successful test proves the full pipeline.

import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Loader2, Mail, ShieldAlert, Send, CheckCircle2, XCircle } from "lucide-react";
import { AuthGate } from "@/components/auth-gate";
import { useAuth } from "@/hooks/use-auth";
import { sendAppEmail, type AppEmailTemplate } from "@/lib/email/send-app";
import {
  AdminShell,
  AdminPageHeader,
  AdminCard,
  AdminCardHeader,
  AdminButton,
  AdminBadge,
  type AdminBadgeTone,
} from "@/components/admin";

export const Route = createFileRoute("/admin/email-test")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Email Testing" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AuthGate allow={["admin"]}>
      <EmailTestPage />
    </AuthGate>
  ),
});

interface TemplateDef {
  name: AppEmailTemplate;
  label: string;
  group: "App" | "Auth";
  sampleData: (name: string) => Record<string, unknown>;
}

const REVIEW_REF = "REV-TEST-24812";

const TEMPLATES: TemplateDef[] = [
  {
    name: "welcome",
    label: "Welcome",
    group: "App",
    sampleData: (n) => ({ customerName: n }),
  },
  {
    name: "auth-signup",
    label: "Email verification",
    group: "Auth",
    sampleData: (_n) => ({
      recipient: "you@example.com",
      confirmationUrl: "https://invoiceintelligenceai.com/verify?token=preview",
    }),
  },
  {
    name: "auth-recovery",
    label: "Password reset",
    group: "Auth",
    sampleData: (_n) => ({
      confirmationUrl: "https://invoiceintelligenceai.com/reset?token=preview",
    }),
  },
  {
    name: "payment-receipt",
    label: "Payment receipt",
    group: "App",
    sampleData: (n) => ({
      customerName: n,
      reviewRef: REVIEW_REF,
      amount: "$149.00",
      paidAt: new Date().toLocaleString(),
      plan: "Professional Review",
      receiptNumber: "IIA-TEST-000241",
    }),
  },
  {
    name: "invoice-uploaded",
    label: "Invoice uploaded",
    group: "App",
    sampleData: (n) => ({
      customerName: n,
      reviewRef: REVIEW_REF,
      fileCount: 2,
      submittedAt: new Date().toLocaleString(),
    }),
  },
  {
    name: "review-started",
    label: "Review started",
    group: "App",
    sampleData: (n) => ({ customerName: n, reviewRef: REVIEW_REF }),
  },
  {
    name: "review-complete",
    label: "Review complete",
    group: "App",
    sampleData: (n) => ({
      customerName: n,
      reviewRef: REVIEW_REF,
      findingsCount: 4,
      potentialSavings: "$612",
    }),
  },
  {
    name: "report-ready",
    label: "Report ready",
    group: "App",
    sampleData: (n) => ({ customerName: n, reviewRef: REVIEW_REF }),
  },
  {
    name: "negotiation-started",
    label: "Negotiation started",
    group: "App",
    sampleData: (n) => ({ customerName: n, reviewRef: REVIEW_REF }),
  },
  {
    name: "negotiation-update",
    label: "Negotiation update",
    group: "App",
    sampleData: (n) => ({
      customerName: n,
      reviewRef: REVIEW_REF,
      updateMessage:
        "The vendor responded to our first outreach and asked for supporting documentation. We're preparing that now.",
    }),
  },
  {
    name: "negotiation-complete",
    label: "Negotiation complete",
    group: "App",
    sampleData: (n) => ({
      customerName: n,
      reviewRef: REVIEW_REF,
      outcome: "Vendor accepted revised total",
      savings: "$438",
    }),
  },
];

type SendStatus = "idle" | "sending" | "sent" | "failed" | "deduped";

function EmailTestPage() {
  const { user, isStaff, isAdmin, loading } = useAuth();

  const [recipient, setRecipient] = useState(user?.email ?? "");
  const [customerName, setCustomerName] = useState(
    ((user?.user_metadata as { full_name?: string } | undefined)?.full_name ??
      "").trim() || "Alex Morgan",
  );
  const [statuses, setStatuses] = useState<Record<string, SendStatus>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [bulkRunning, setBulkRunning] = useState(false);

  const authorized = isStaff || isAdmin;

  async function sendOne(t: TemplateDef) {
    if (!recipient) return;
    setStatuses((s) => ({ ...s, [t.name]: "sending" }));
    setErrors((e) => ({ ...e, [t.name]: "" }));
    const key = `admin-test:${t.name}:${Date.now()}`;
    const res = await sendAppEmail({
      templateName: t.name,
      recipientEmail: recipient,
      idempotencyKey: key,
      templateData: t.sampleData(customerName),
    });
    if (res.ok) {
      setStatuses((s) => ({
        ...s,
        [t.name]: res.deduped ? "deduped" : "sent",
      }));
    } else {
      setStatuses((s) => ({ ...s, [t.name]: "failed" }));
      setErrors((e) => ({
        ...e,
        [t.name]: res.reason ?? `HTTP ${res.status ?? "?"}`,
      }));
    }
  }

  async function sendAll() {
    setBulkRunning(true);
    for (const t of TEMPLATES) {
      // Sequential to keep the queue orderly and per-row status clear.
      await sendOne(t);
    }
    setBulkRunning(false);
  }

  const grouped = useMemo(() => {
    return {
      App: TEMPLATES.filter((t) => t.group === "App"),
      Auth: TEMPLATES.filter((t) => t.group === "Auth"),
    };
  }, []);

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking access…
        </div>
      </Shell>
    );
  }

  if (!authorized) {
    return (
      <Shell>
        <AdminCard>
          <div className="flex items-center gap-2 text-sm font-semibold text-navy">
            <ShieldAlert className="h-4 w-4 text-warn" /> Restricted
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Email testing is available to staff and administrators only.
          </p>
          <Link
            to="/dashboard"
            className="mt-4 inline-flex text-xs font-semibold uppercase tracking-wider text-trust hover:underline"
          >
            Back to dashboard
          </Link>
        </AdminCard>
      </Shell>
    );
  }

  return (
    <Shell>
      <AdminPageHeader
        kicker="Admin · Diagnostics"
        title="Email testing"
        description="Send any branded template to a recipient of your choice. Uses the production Lovable Emails pipeline end-to-end."
      />

      <AdminCard>
        <AdminCardHeader
          title="Test recipient"
          description="Configure once, then send individual templates or the full suite."
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm">
            <span className="mb-1 block font-semibold text-navy">
              Recipient email
            </span>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-navy/20"
              placeholder="you@example.com"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-semibold text-navy">
              Customer name (used in templates)
            </span>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-navy/20"
            />
          </label>
          <div className="sm:col-span-2">
            <AdminButton
              type="button"
              onClick={sendAll}
              disabled={!recipient}
              loading={bulkRunning}
              icon={<Send />}
            >
              Send every template to {recipient || "recipient"}
            </AdminButton>
          </div>
        </div>
      </AdminCard>

      {(["App", "Auth"] as const).map((group) => (
        <div key={group} className="mt-6">
          <AdminCard>
            <AdminCardHeader title={`${group} emails`} />
            <ul className="divide-y divide-border">
              {grouped[group].map((t) => {
                const status = statuses[t.name] ?? "idle";
                return (
                  <li
                    key={t.name}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-3 sm:flex sm:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-semibold text-navy">
                        <Mail className="h-4 w-4 shrink-0 text-trust" />
                        <span className="truncate">{t.label}</span>
                      </div>
                      <div className="mt-0.5 truncate text-[11px] uppercase tracking-wider text-muted-foreground">
                        {t.name}
                      </div>
                      {errors[t.name] ? (
                        <div className="mt-1 text-xs text-destructive">
                          {errors[t.name]}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <StatusBadge status={status} />
                      <AdminButton
                        variant="secondary"
                        size="sm"
                        onClick={() => sendOne(t)}
                        disabled={!recipient}
                        loading={status === "sending"}
                        icon={<Send />}
                      >
                        Send test
                      </AdminButton>
                    </div>
                  </li>
                );
              })}
            </ul>
          </AdminCard>
        </div>
      ))}

      <p className="mt-6 text-xs text-muted-foreground">
        Delivery is asynchronous. If DNS is still verifying, sends are queued
        and will flush automatically once the domain goes live. Check{" "}
        <span className="font-semibold text-navy">Cloud → Emails</span> for
        send logs and suppression state.
      </p>
    </Shell>
  );
}

function StatusBadge({ status }: { status: SendStatus }) {
  if (status === "idle") return null;
  const map: Record<
    SendStatus,
    { label: string; tone: AdminBadgeTone; icon: React.ReactNode } | null
  > = {
    idle: null,
    sending: {
      label: "Sending",
      tone: "muted",
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
    },
    sent: {
      label: "Queued",
      tone: "success",
      icon: <CheckCircle2 className="h-3 w-3" />,
    },
    deduped: {
      label: "Deduped",
      tone: "info",
      icon: <CheckCircle2 className="h-3 w-3" />,
    },
    failed: {
      label: "Failed",
      tone: "danger",
      icon: <XCircle className="h-3 w-3" />,
    },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <AdminBadge tone={s.tone} icon={s.icon}>
      {s.label}
    </AdminBadge>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <AdminShell>
      <div className="mx-auto max-w-3xl">{children}</div>
    </AdminShell>
  );
}
